/**
 * Utility Functions for North Star UI
 * Phase 2B BIF Milestone
 * 
 * BIF MILESTONE: getModeLabel() mapping changed from 'transactional' to 'pick'
 */

import type { PoolStatus, PoolDomain, PoolMode } from './types';

// ========================================
// Date/Time Utilities
// ========================================

/**
 * Format ISO date string to readable format
 */
export function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format ISO date string to readable date and time
 */
export function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Get relative time string (e.g., "2 hours ago")
 */
export function getRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return formatDate(isoString);
}

// ========================================
// Number Formatting
// ========================================

/**
 * Format number as currency (no cents for bankroll)
 */
export function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

/**
 * Format percentage with sign
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  const formatted = value.toFixed(decimals);
  return value > 0 ? `+${formatted}%` : `${formatted}%`;
}

/**
 * Format number with commas
 */
export function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

// ========================================
// Pool Status Utilities
// ========================================

/**
 * Get CSS class for pool status badge
 */
export function getStatusBadgeClass(status: PoolStatus): string {
  switch (status) {
    case 'active':
      return 'badge-green';
    case 'pending':
      return 'badge-amber';
    case 'locked':
    case 'completed':
      return 'badge-gray';
    case 'cancelled':
      return 'badge-red';
    default:
      return 'badge-gray';
  }
}

/**
 * Get human-readable status label
 */
export function getStatusLabel(status: PoolStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Check if pool is editable (Q6: non-structural edits only)
 */
export function isPoolEditable(status: PoolStatus): boolean {
  return status === 'pending' || status === 'active';
}

// ========================================
// Domain/Mode Utilities
// ========================================

/**
 * Get domain display name
 */
export function getDomainLabel(domain: PoolDomain): string {
  const labels: Record<PoolDomain, string> = {
    nfl: 'NFL',
    college: 'College Football',
    nba: 'NBA',
    mlb: 'MLB',
    nhl: 'NHL',
    soccer: 'Soccer',
    corporate: 'Corporate',
    financial: 'Financial',
  };
  return labels[domain] || domain;
}

/**
 * Get mode display name
 * 
 * BIF MILESTONE: Changed key from 'transactional' to 'pick'
 */
export function getModeLabel(mode: PoolMode): string {
  const labels: Record<PoolMode, string> = {
    gm: 'GM Mode',
    pick: 'Pick Mode',
  };
  return labels[mode] || mode;
}

// ========================================
// Validation Utilities
// ========================================

/**
 * Validate pool ID format
 */
export function isValidPoolId(poolId: string): boolean {
  return /^[a-z0-9_]+$/.test(poolId);
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Validate positive number
 */
export function isPositiveNumber(value: string | number): boolean {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return !isNaN(num) && num > 0;
}

// ========================================
// BSL Utilities (BSL-Compliant)
// ========================================

/**
 * Q9: Get confidence drift display text (approved wording)
 * BSL-compliant: factual description only
 */
export function getConfidenceDriftText(drift: number): string {
  if (Math.abs(drift) < 0.05) {
    return 'Confidence levels remain consistent across predictions';
  } else if (drift > 0) {
    return `Confidence levels trending upward by ${(drift * 100).toFixed(1)} percentage points`;
  } else {
    return `Confidence levels trending downward by ${(Math.abs(drift) * 100).toFixed(1)} percentage points`;
  }
}

/**
 * Format stake distribution for display
 */
export function formatStakeDistribution(distribution: Record<number, number>): Array<{ amount: number; count: number }> {
  return Object.entries(distribution)
    .map(([amount, count]) => ({
      amount: parseInt(amount),
      count: count,
    }))
    .sort((a, b) => b.count - a.count);
}

// ========================================
// User/Admin Utilities
// ========================================

/**
 * Q12: Check if user ID is admin
 */
export function isAdminUser(userId: string): boolean {
  const adminIds = (import.meta.env.VITE_ADMIN_USER_IDS || 'admin').split(',');
  return adminIds.includes(userId.trim());
}

/**
 * Get user display name (fallback to ID)
 */
export function getUserDisplayName(userId: string): string {
  return userId
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ========================================
// Error Handling Utilities
// ========================================

/**
 * Extract error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
}

// ========================================
// Local Storage Utilities
// ========================================

/**
 * Safe localStorage get
 */
export function getLocalStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Safe localStorage set
 */
export function setLocalStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
}

// ========================================
// Array Utilities
// ========================================

/**
 * Group array by key
 */
export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((result, item) => {
    const groupKey = String(item[key]);
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {} as Record<string, T[]>);
}

/**
 * Sort array by key
 */
export function sortBy<T>(array: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}

// ========================================
// DOM Utilities
// ========================================

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  }
}

/**
 * Scroll to top of page
 */
export function scrollToTop(): void {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}
